import { Component, DestroyRef, effect, input, OnInit, output, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SessionService } from '../../../services/session.service';
import { Profile, UserService } from '@eadsysteme/ewsauthclient-v1';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { Icons } from '../../../util/Icons';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogoutService } from '../../../../@vex/services/logout.service';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';

@Component({
	selector: 'vex-update-mail',
	templateUrl: './update-mail.component.html',
	styleUrl: './update-mail.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslocoDirective, MatFormField, NgClass, DefaultClassDirective, MatLabel, MatInput, ReactiveFormsModule, MatError],
})
export class UpdateMailComponent implements OnInit {
	translocoService = inject(TranslocoService);
	sessionService = inject(SessionService);
	layoutService = inject(LayoutService);
	userService = inject(UserService);
	icons = inject(Icons);
	snackbar = inject(SnackbarService);
	keycloak = inject(KeycloakService);
	private dialog = inject(MatDialog);
	private logoutService = inject(LogoutService);
	private destroyRef = inject(DestroyRef);

	mailAddressFC = new UntypedFormControl(
		{
			value: '',
			disabled: true,
		},
		[Validators.email, Validators.required, Validators.minLength(1)]
	);
	isMobile = signal(false);
	errorMsg = signal('');

	isEdit = input<boolean>();
	readonly update = input<Observable<void>>(undefined);

	allowUpdate = output<boolean>();
	formChanged = output<boolean>();

	constructor() {
		effect(() => {
			if (this.isEdit()) {
				this.mailAddressFC.enable();
			} else {
				this.mailAddressFC.disable();
			}
		});
	}

	ngOnInit(): void {
		this.isMobile.set(this.layoutService.isMobile());
		const keycloakToken = this.keycloak.getKeycloakInstance().tokenParsed;
		this.mailAddressFC.setValue(keycloakToken.preferred_username);

		this.update()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.confirmUpdateMail();
			});
	}

	checkMailValidator() {
		this.formChanged.emit(this.mailAddressFC.dirty);
		this.mailAddressFC.markAllAsTouched();
		if (!this.mailAddressFC.value) {
			this.mailAddressFC.setErrors(null);
			this.errorMsg.set('');
			this.allowUpdate.emit(false);
			return true;
		}
		this.errorMsg.set(this.mailAddressFC.valid ? '' : this.translocoService.translate('userProfile.invalidMail'));
		this.allowUpdate.emit(this.mailAddressFC.valid);
		return this.mailAddressFC.valid;
	}

	confirmUpdateMail() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('userProfile.confirmNewMail');

		dialogRef.afterClosed().subscribe((val) => {
			if (val) {
				this.updateMail();
			}
		});
	}

	updateMail() {
		const mail: Profile = {
			email: this.mailAddressFC.value,
		};
		const userId = this.keycloak.getKeycloakInstance().tokenParsed.sub;
		if (this.allowUpdate) {
			this.userService.updateOwnEmail(userId, mail).subscribe({
				next: (response) => {
					if (response) {
						// Success Message and Logout
						this.notifyUserAndLogout();
					} else {
						this.snackbar.error(this.translocoService.translate('userProfile.mailUpdateBadInput'));
					}
				},
				error: () => {
					this.snackbar.error(this.translocoService.translate('errors.userProfileMailUpdate'));
				},
			});
		}
	}

	notifyUserAndLogout() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.showCloseButton = false;
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate('userProfile.mailUpdatedSuccessfully');
		dialogRef.componentInstance.confirmButtonText = this.translocoService.translate('common.ok');

		dialogRef.afterClosed().subscribe(() => {
			this.logoutService.logout();
		});
	}
}
